import Vue from 'vue';
import Component from 'vue-class-component';
import clone from 'clone';
import { Prop, Watch } from 'vue-property-decorator';
import { ResponseError } from '../../../wc-http-client/response-errors';
import { TableColumn, SortDirection, FixedColumn, FieldType } from './table-column';
import { TableBody, TableNavigation } from './table-body';
import { TableHeader } from './table-header';
import { focusDirective } from '../../directives/focus';
import { TableAlert } from './table-alert';
import jsonpath from 'jsonpath';
import { isString } from '../../../wc-converters/util';

require('../../../../icons/arrowright.svg');
require('../../../../icons/arrowleft.svg');

Vue.directive('focus', focusDirective);

@Component({
    template: require('./table.html'),
    name: 'wc-table',
    components: {
        'wc-table-body': TableBody,
        'wc-table-header': TableHeader
    }
})
export class Table extends Vue {
    @Prop()
    rowIdField: string;
    @Prop()
    data: Array<any>;
    @Prop()
    columns: Array<TableColumn>;
    @Prop()
    fixedHeader: boolean;
    @Prop()
    rowSelection: (selectedRows: Array<any>) => void;
    @Prop()
    expandedRowField: string;
    @Prop()
    expandedRowFormatter: (row: any) => string;
    @Prop()
    expandedRowComponent: string;
    @Prop()
    expandedRowComponentField: string;
    @Prop()
    rowClick: (row: any) => void;
    @Prop()
    rowDoubleClick: (row: any) => void;
    @Prop()
    rowClass: (row: any) => void;
    @Prop()
    small: boolean;
    @Prop({
        default: true
    })
    cloneData: boolean;
    @Prop({
        default: false
    })
    paging: boolean;
    @Prop({
        default: 10
    })
    pageLength: number;
    @Prop({
      default: 1
    })
    currentPageDefault: number;
    @Prop({
        default: 2
    })
    clicksToEdit: number;
    // Called when a table cell editor modifies table data
    // Returns the latest modified row/column and all modified rows
    @Prop()
    dataEdited: (row: any, column: TableColumn, data: Array<any>) => void;
    @Prop({
        default: false
    })
    navigatable: boolean;
    @Prop()
    dataErrors: Array<ResponseError>;
    @Prop()
    dataErrorsPath: string;
    @Prop()
    disableVirtualScroll: boolean;
    @Prop()
    focusedRow: any;
    // JSON path
    @Prop()
    focusedError: string;

    @Prop({
        default: false
    })
    selectAllRows: boolean;

    rows: Array<any> = [];
    lastScrollTop: number;
    lastScrollLeft: number;
    scrollBarWidth: number;
    fixedColumnsLeftShadow: boolean = false;
    currentPage: number = 1;
    currentPageSet: number = 1;
    groups: Array<any> = [];
    totalColumns: Array<TableColumn> = [];
    sortedByColumns: Array<TableColumn> = [];
    navigation: TableNavigation | null = null;
    tableAlerts: Array<TableAlert> = [];
    virtualScroll = {
        bufferSize: 6,
        rowHeight: 40,
        smallRowHeight: 30
    };

    get expandableRow(): boolean {
        return this.expandedRowField || this.expandedRowFormatter || this.expandedRowComponent || this.columns.some(x => x.group === true && x.hidden !== true) ? true : false;
    }

    get groupColumn(): any {
        return this.columns.find(x => x.group === true);
    }

    get fixedColumnsLeft() {
        let leftColumns = this.fixedColumn(FixedColumn.left);
        if (leftColumns.length > 0) {
            leftColumns.unshift(...this.groupColumnsByFixed(undefined));
        }
        return leftColumns;
    }

    get fixedColumnsRight() {
        return this.fixedColumn(FixedColumn.right);
    }

    get pageCount() {
        return Math.ceil(this.rows.length / this.pageLength);
    }

    get rangeFrom() {
        let rangeFrom = this.currentPage * this.pageLength - this.pageLength + 1;
        if (rangeFrom < 1)
            rangeFrom = 1;

        return rangeFrom;
    }

    get rangeTo() {
        let rangeTo = this.currentPage * this.pageLength;

        if (rangeTo > this.rows.length)
            rangeTo = this.rows.length;
        else if (rangeTo < this.pageLength)
            rangeTo = this.pageLength;

        return rangeTo;
    }

    @Watch('pageLength')
    updatePageLength(){
        // reset current page to last page if change is larger then max
        if(this.currentPage>this.pageCount){
            this.currentPageSet=this.pageCount;
        }
    }
    @Watch('currentPageSet')
    @Watch('currentPageDefault')
    updateCurrentPage(newPageNumber:number){
        // allow null editing
        if (newPageNumber || newPageNumber === 0){
            // validate/sanity check entry before changing current page
            if (newPageNumber < 1 || this.pageCount < 1){
                this.currentPageSet = 1;
            } else if (newPageNumber > this.pageCount){
                this.currentPageSet = this.pageCount;
            } else if (this.pageCount !== Math.floor(newPageNumber)){
                this.currentPageSet = Math.floor(newPageNumber);
            } else {
                this.currentPageSet = this.pageCount;
            }
            // render if there is a change
            if (this.currentPage != this.currentPageSet){
                this.currentPage = this.currentPageSet;
                this.$emit('currentPageChange', this.currentPage);
            }
        }
    }

    @Watch('data', { deep: false })
    dataChange(newValue: Array<any>, oldValue?: Array<any>) {
        if (this.cloneData)
            this.rows = clone(newValue);
        else
            this.rows = newValue;

        // Prevent endless data watcher loop, sorting table rows causes the data watcher to fire. 
        // Call sort table rows if data object has been reassigned and only update display if sort columns are found.
        if (newValue !== oldValue) {
            this.updateSort(true);
        }

        this.updateDisplay();
        
        // current page is larger then new data amount reset to largest
        if(this.currentPage>this.pageCount){
            this.currentPageSet=this.pageCount;
        }
    }

    @Watch('dataErrors')
    normaliseErrors() {
        this.tableAlerts = [];
        if (!this.dataErrors) return;

        for (let error of clone(this.dataErrors) as Array<TableAlert>) {
            if (isString(error.source) && this.dataErrorsPath) {
                let source = error.source.replace(this.dataErrorsPath, '$..data');
                let nodes = jsonpath.nodes({ data: this.rows }, source);
                if (nodes.length > 0) {
                    let node = nodes[0];
                    error.row = this.rows[node.path[2]];
                    if (node.path.length === 4) {
                        error.field = node.path[3];
                    } else if (node.path.length === 5 && !isNaN(node.path[4])) {
                        error.field = `${node.path[3]}[${node.path[4]}]`;
                    }
                    this.tableAlerts.push(error);
                }
            }
        }
    }

    @Watch('focusedRow')
    focusedRowChange() {
        this.scrollToRow(this.focusedRow);
    }

    @Watch('focusedError')
    focusedErrorChange() {
        if (this.focusedError) {
            let alertRow = this.tableAlerts.find(alertItem => alertItem.source === this.focusedError);
            this.scrollToRow(alertRow);
        }
    }

    updateSort(force: boolean = false) {
        let sortedColumns = this.columns.filter((column) => column.sortDirection === 0 || column.sortDirection === 1);

        let groupColumn = this.groupColumn;
        if (groupColumn) {
            sortedColumns.unshift(groupColumn);
        }

        if (sortedColumns.length > 0) {
            if (force === true) {
                this.sort(sortedColumns);
                return true;
            } else {
                let sortUnchanged = (sortedColumns.length == this.sortedByColumns.length) && sortedColumns.every((column, index) => {
                    return column === this.sortedByColumns[index];
                });

                if (!sortUnchanged) {
                    this.sort(sortedColumns);
                    return true;
                }
            }
        }

        return false;
    }

    updateDisplay() {
        this.cacheSettings();
        this.setupGroups();
        this.scrollStyles();
        this.bodyStyles();
    }

    @Watch('groups', { deep: true })
    groupsChange() {
        this.$nextTick(this.updateStyles);
    }

    @Watch('columns', { deep: true })
    columnsChange() {
        this.updateSort();
        this.updateDisplay();
    }

    created() {
        window.addEventListener('resize', this.updateStyles);
        this.scrollBarWidth = this.scrollbarWidth();
        // set current page to default props value
        this.currentPageSet = this.currentPageDefault;
    }

    beforeMount() {
        this.dataChange(this.data);
        this.normaliseErrors();
    }

    mounted() {
        this.updateStyles();

        if (this.selectAllRows) {
            this.selectAll(this.selectAllRows);
        }
    }

    cacheSettings() {
        this.totalColumns = this.columns.filter(x => x.total);
    }

    updateStyles() {
        this.scrollStyles();
        this.bodyStyles();
    }


    scroll(e) {
        this.updateVirtualScrollItems();
        this.scrollVertical(e);
        this.scrollHorizontal(e);
    }

    scrollVertical(e) {
        const target = e.target;
        if (e.currentTarget !== target) {
            return;
        }
        let fixedColumnsLeftBodyContainer = this.$refs.fixedColumnsLeftBodyContainer as HTMLElement;
        let tableBody = this.$refs.tableBodyContainer as HTMLElement;

        if (target.scrollTop !== this.lastScrollTop) {
            const scrollTop = target.scrollTop;
            if (fixedColumnsLeftBodyContainer && target !== fixedColumnsLeftBodyContainer) {
                fixedColumnsLeftBodyContainer.scrollTop = scrollTop;
            }

            if (tableBody && target !== tableBody) {
                tableBody.scrollTop = scrollTop;
            }
        }

        this.lastScrollTop = target.scrollTop;
    }

    scrollHorizontal(e) {
        if (e.currentTarget !== e.target) {
            return;
        }
        const target = e.target;
        let tableBody = this.$refs.tableBodyContainer as HTMLElement;
        let tableHeader = this.$refs.tableHeader as HTMLElement;

        if (target.scrollLeft !== this.lastScrollLeft) {
            if (target === tableBody && tableHeader) {
                tableHeader.scrollLeft = target.scrollLeft;
            } else if (target === tableHeader && tableBody) {
                tableBody.scrollLeft = target.scrollLeft;
            }
            this.scrollPositionStyles();
        }

        this.lastScrollLeft = target.scrollLeft;
    }

    scrollbarWidth() {
        let outer = document.createElement("div");
        outer.style.visibility = "hidden";
        outer.style.width = "100px";
        // prevent type cast for browser specific styles
        outer.style["msOverflowStyle"] = "scrollbar"; // needed for WinJS apps

        document.body.appendChild(outer);

        let widthNoScroll = outer.offsetWidth;
        // force scrollbars
        outer.style.overflow = "scroll";

        // add innerdiv
        let inner = document.createElement("div");
        inner.style.width = "100%";
        outer.appendChild(inner);

        let widthWithScroll = inner.offsetWidth;

        // remove divs
        outer.parentNode!.removeChild(outer);

        return widthNoScroll - widthWithScroll;
    }

    hasVerticalScroll() {
        let tableBody = this.$refs.tableBodyContainer as HTMLElement;
        if (tableBody) {
            let table = tableBody.firstChild as HTMLElement;
            if (table.offsetWidth < tableBody.offsetWidth) return true;
        }
        return false;
    }

    hasHorizontalScroll() {
        let tableBody = this.$refs.tableBody as HTMLElement;

        if (tableBody) {
            let table = tableBody.firstChild as HTMLElement;
            if (table.clientHeight > tableBody.clientHeight) return true;
        }
        return false;
    }

    scrollPositionStyles() {
        let tableBody = this.$refs.tableBodyContainer as HTMLElement;
        let fixedColumnsLeft = this.$refs.fixedColumnsLeft as HTMLElement;

        this.fixedColumnsLeftShadow = tableBody.scrollLeft !== 0;
    }

    scrollStyles() {
        let fixedColumnsLeft = this.$refs.fixedColumnsLeft as HTMLElement;
        let tableHeader = this.$refs.tableHeader as HTMLElement;
        let fixedColumnsLeftBodyContainer = this.$refs.fixedColumnsLeftBodyContainer as HTMLElement;

        if (fixedColumnsLeft)
            fixedColumnsLeft.style.bottom = `${this.scrollBarWidth}px`;
        if (this.hasVerticalScroll()) {
            tableHeader.style.paddingRight = `${this.scrollBarWidth}px`;
            if (fixedColumnsLeftBodyContainer)
                fixedColumnsLeftBodyContainer.style.paddingRight = `-${this.scrollBarWidth}px`;
        }
    }

    bodyStyles() {
        let tableHeader = this.$refs.tableHeader as HTMLElement;
        let tableBody = this.$refs.tableBody as HTMLElement;
        let fixedTableHeaderLeft = this.$refs.fixedTableHeaderLeft as HTMLElement;

        if (tableHeader && tableBody) {
            tableBody.style.marginTop = `-${tableHeader.offsetHeight}px`;

            let bodyColumns = tableBody.querySelectorAll('thead tr th');
            let headerColumns = tableHeader.querySelectorAll('thead tr th');
            let headerColumnsLeft: Array<HTMLElement> = [];
            if (fixedTableHeaderLeft) {
                headerColumnsLeft = Array.prototype.slice.call(fixedTableHeaderLeft.querySelectorAll('thead tr th'));
            }

            Array.prototype.forEach.call(bodyColumns, function (item, index) {
                let headerColumn = headerColumns[index] as HTMLElement;
                headerColumn.style.minWidth = `${item.clientWidth}px`;

                let leftColumn = headerColumnsLeft.find(x => x.innerText === headerColumn.innerText);

                if (leftColumn) {
                    leftColumn.style.minWidth = `${item.clientWidth}px`;
                }
            });
        }
    }

    sort(columns: Array<TableColumn>) {
        if (!Array.isArray(columns)) {
            columns = [columns];
        }
        this.sortedByColumns = columns;

        this.rows.sort((a, b) => {
            let result;
            for (var i = 0; i < columns.length && !result; i++) {
                // bug fix ascii order is case sensitive for string comparison
                if(columns[i].fieldType === FieldType.String){
                    // for sorting we default to ""
                    let curA = "", curB = "";
                    if(a[columns[i].field]){
                        curA = a[columns[i].field].toLowerCase();
                    }
                    if(b[columns[i].field]){
                        curB = b[columns[i].field].toLowerCase();
                    }
                    result = this.itemCompare(curA, curB, columns[i].sortDirection);
                } else {
                    result = this.itemCompare(a[columns[i].field], b[columns[i].field], columns[i].sortDirection);
                }
            }
            return result;
        });

        this.$nextTick(() => {
            this.updateStyles();
            this.updateVirtualScrollItems();
            let tableBody = this.$refs.tableBodyContainer as HTMLElement;
            let tableHeader = this.$refs.tableHeader as HTMLElement;
            // sanity check nulls before calling scrollLeft
            if(tableHeader && tableBody){
                tableHeader.scrollLeft = tableBody.scrollLeft;
            }
            // emit the event for user binding
            this.$emit('sortClick', columns);
        });
    }

    itemCompare(a: any, b: any, sortDirection?: SortDirection) {
        if (a < b) {
            return (sortDirection === SortDirection.Descending) ? 1 : -1;
        }
        else if (a > b) {
            return (sortDirection === SortDirection.Descending) ? -1 : 1;
        }
        else {
            return 0;
        }
    }

    selectChange() {
        let selectedRows = clone(this.rows.filter((item) => item.wcSelected));
        for (let row of selectedRows) delete row.wcSelected;
        this.rowSelection.call(this, selectedRows);
    }

    selectAll(selected: boolean) {
        for (let i = 0; i < this.rows.length; i++) {
            let row = this.rows[i];
            if (selected) row.wcSelected = true;
            else delete row.wcSelected;

            this.rows.splice(i, 1, row);
        }

        this.selectChange();
    }

    dataEdit(row, column) {
        let modifiedRows = clone(this.rows.filter((item) => item.__dirty));
        if (this.dataEdited) this.dataEdited.call(this, row, column, modifiedRows);
    }

    fixedColumn(fixed: FixedColumn) {
        return this.columns.filter(column => { return column.fixed === fixed && column.hidden !== true });
    }

    groupColumnsByFixed(fixed: FixedColumn | undefined) {
        return this.columns.filter(column => { return column.fixed === fixed && column.group === true && column.hidden !== true });
    }

    nextPage() {
        if (this.currentPage === this.pageCount) return;

        this.currentPageSet = this.currentPage + 1;

        this.$nextTick(() => {
            this.updateStyles();
        });
    }

    previousPage() {
        if (this.currentPage === 1) return;

        this.currentPageSet = this.currentPage - 1;

        this.$nextTick(() => {
            this.updateStyles();
        });
    }

    setupGroups() {
        let groupColumn = this.groupColumn;
        let groups: Array<any> = [];

        if (!groupColumn) {
            let expandedGroup = {
                value: null,
                expanded: true,
                data: this.rows,
                hideRow: true
            };

            if (this.totalColumns.length > 0) {
                expandedGroup['totals'] = {};
                for (let item of this.rows) {
                    this.groupTotal(expandedGroup, item);
                }
            }

            // reset groups
            this.groups = [];

            if (this.$refs.tableBodyContainer) {
                // Table is already mounted so render virtualised items immediately 
                this.groups.push(expandedGroup);
                this.updateVirtualScrollItems();
            } else {
                // Render empty list to speed up initial load time and add virtualised 
                // scroll items in the next DOM update cycle.
                this.$nextTick(() => {
                    this.groups.push(expandedGroup);
                    this.updateVirtualScrollItems();
                });
            }
            return;
        }

        for (let item of this.rows) {

            let expandedGroup = groups.find(x => x.value === item[groupColumn.field]);

            if (!expandedGroup) {
                // Maintain previously expanded groups
                let cachedExpandedGroup = this.groups.find(x => x.value === item[groupColumn.field]);

                expandedGroup = {
                    value: item[groupColumn.field],
                    expanded: cachedExpandedGroup ? cachedExpandedGroup.expanded : false,
                    data: []
                };
                if (this.totalColumns.length > 0) {
                    expandedGroup['totals'] = {};
                }
                groups.push(expandedGroup);
            }
            this.groupTotal(expandedGroup, item);
            expandedGroup.data.push(item);
        }

        this.groups = groups;
    }

    groupTotal(group, row) {
        for (let column of this.totalColumns) {
            if (group.totals[column.field] === undefined) {
                group.totals[column.field] = 0;
            }
            group.totals[column.field] += row[column.field];
        }
    }

    updateVirtualScrollItems() {
        if (this.groupColumn || this.disableVirtualScroll === true) return;

        let tableBodyContainer = this.$refs.tableBodyContainer as HTMLElement;
        let tableBody = this.$refs.tableBody as HTMLElement;
        let leftTableBody = this.$refs.fixedColumnsLeftBody as HTMLElement;
        let rowHeight = this.small === true ? this.virtualScroll.smallRowHeight : this.virtualScroll.rowHeight;


        if (tableBodyContainer && tableBody) {
            let scrollBottom = tableBodyContainer.scrollTop + tableBodyContainer.clientHeight;
            let startIndex = Math.floor(tableBodyContainer.scrollTop / rowHeight) - this.virtualScroll.bufferSize;
            if (startIndex < 0) startIndex = 0;

            let endIndex = Math.ceil(scrollBottom / rowHeight) + this.virtualScroll.bufferSize;
            if (endIndex >= this.rows.length) endIndex = this.rows.length;

            tableBody.style.paddingTop = startIndex * rowHeight + 'px';
            tableBody.style.height = this.rows.length * rowHeight + (tableBody.style.marginTop ? Math.abs(+ tableBody.style.marginTop.replace("px", "")) : 0) + 'px';
            this.groups[0].data = this.rows.slice(startIndex, endIndex);
            // only configure leftTableBody if valid as it is is an optional config 
            if (leftTableBody) {
                leftTableBody.style.paddingTop = startIndex * rowHeight + 'px';
                leftTableBody.style.height = this.rows.length * rowHeight + (tableBody.style.marginTop ? Math.abs(+ tableBody.style.marginTop.replace("px", "")) : 0) + 'px';
            }

        }
    }

    scrollToRow(row: any) {
        // TODO - add group column support.
        if (this.groupColumn || !row) return;

        let tableBodyContainer = this.$refs.tableBodyContainer as HTMLElement;
        let tableBody = this.$refs.tableBody as HTMLElement;
        let rowHeight = this.small === true ? this.virtualScroll.smallRowHeight : this.virtualScroll.rowHeight;

        if (tableBodyContainer && tableBody) {
            let rowIndex = this.rows.indexOf(row);

            // make sure pagination tables rowIndex is reset per page
            if (this.paging && this.currentPageDefault > 1) {
                rowIndex = rowIndex - (this.pageLength * (this.currentPageDefault - 1))
            }

            let scrollTop = Math.ceil(rowIndex * rowHeight);

            // If browser supports scrollTo use smooth scroll
            if (tableBodyContainer.scrollTo) {
                tableBodyContainer.scrollTo({
                    top: scrollTop,
                    behavior: 'smooth'
                });
            } else {
                tableBodyContainer.scrollTop = scrollTop;
            }
        }
    }

    destroyed() {
        window.removeEventListener('resize', this.updateStyles);
    }
}

Vue.filter('tablePage', (groups: Array<any>, paging: boolean, rangeFrom: number, rangeTo: number) => {
    if (paging) {
        let total = 0;
        let fromTotal = rangeFrom - 1;
        let toTotal = rangeTo;
        let pagedGroups: Array<any> = [];

        for (let group of groups) {
            let oldTotal = total;
            total += group.data.length;

            if (total > fromTotal && total < toTotal) {
                if (group.data.length < fromTotal) {
                    // only take/slice the last part of the previous group if half of it is already displayed on the previous page
                    if(total >= oldTotal + group.data.length){
                        group.pageData = group.data.slice(fromTotal - oldTotal, group.data.length);
                    }else{
                        group.pageData = group.data;
                    }
                } else {
                    group.pageData = group.data.slice(fromTotal, total);
                }
                pagedGroups.push(group);
            } else if (total > fromTotal && total >= toTotal) {
                group.pageData = group.data.slice(Math.max(0, fromTotal - oldTotal), toTotal - oldTotal);
                if (group.pageData.length > 0)
                    pagedGroups.push(group);
                break;
            }
        }
        return pagedGroups;
    } else {
        return groups;
    }
});



// WEBPACK FOOTER //
// node_modules/wc-lib/src/wc-ui/components/table/table.ts